import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Fuel, Calendar, Circle, Edit, Trash2, Plus } from 'lucide-react';

const VD_AXIS_TICK = { fontSize: 11, fill: '#64748B' };
const VD_TOOLTIP_STYLE = { borderRadius: 8, border: '1px solid #E2E8F0' };
const BAR_RADIUS_TOP = [4, 4, 0, 0];

const COST_TYPE_ICONS = { fuel: Fuel, tyres: Circle, service: Circle, maintenance: Circle, insurance: Circle, other: Circle };
const COST_TYPE_COLORS = {
  fuel: 'bg-blue-100 text-blue-600', tyres: 'bg-amber-100 text-amber-600',
  service: 'bg-red-100 text-red-600', maintenance: 'bg-pink-100 text-pink-600',
  insurance: 'bg-purple-100 text-purple-600', other: 'bg-gray-100 text-gray-600',
};

const fmtR = (v) => {
  if (v == null || isNaN(v)) return 'R 0.00';
  return `R ${Number(v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const VehicleCostsTab = ({
  chartData, filteredCosts, costFilter, setCostFilter,
  fuelEfficiencyMap, setCostDialogOpen, setSelectedCost,
  setEditCostDialogOpen, setDeleteDialogOpen,
}) => (
  <>
    {chartData.length > 0 && (
      <Card className="card-hover">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-heading font-semibold">Monthly Costs</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData}>
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={VD_AXIS_TICK} />
              <YAxis axisLine={false} tickLine={false} tick={VD_AXIS_TICK} tickFormatter={(v) => `R${v}`} />
              <Tooltip formatter={(v) => [`R${v.toFixed(2)}`, 'Cost']} contentStyle={VD_TOOLTIP_STYLE} />
              <Bar dataKey="amount" fill="#0F172A" radius={BAR_RADIUS_TOP} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    )}
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg font-heading font-semibold">Cost History</CardTitle>
        <Select value={costFilter} onValueChange={setCostFilter}>
          <SelectTrigger className="w-[130px]" data-testid="cost-filter-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="fuel">Fuel</SelectItem>
            <SelectItem value="tyres">Tyres</SelectItem>
            <SelectItem value="service">Service</SelectItem>
            <SelectItem value="labour">Labour</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {filteredCosts.length > 0 ? (
          <div className="space-y-3">
            {filteredCosts.map((cost) => {
              const Icon = COST_TYPE_ICONS[cost.cost_type] || Circle;
              const eff = fuelEfficiencyMap[cost.id];
              return (
                <div key={cost.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-md hover:bg-slate-100 transition-colors" data-testid={`cost-entry-${cost.id}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-md flex items-center justify-center ${COST_TYPE_COLORS[cost.cost_type]}`}><Icon className="w-5 h-5" /></div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900 capitalize">{cost.cost_type}</span>
                        {cost.description && <span className="text-slate-500">{cost.description}</span>}
                        {eff && eff.kml != null && (
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full font-data ${eff.kml >= 10 ? 'bg-green-100 text-green-700' : eff.kml >= 7 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`} data-testid={`kml-badge-${cost.id}`}>
                            {eff.kml} km/L
                          </span>
                        )}
                        {eff && eff.lph != null && (
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full font-data ${eff.lph <= 2 ? 'bg-green-100 text-green-700' : eff.lph <= 4 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`} data-testid={`lph-badge-${cost.id}`}>
                            {eff.lph.toFixed(2)} L/hr
                          </span>
                        )}
                        {cost.cost_type === 'fuel' && cost.liters && !eff && (cost.odometer || cost.hours) && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-400 font-data">first fill</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{cost.date}</span>
                        {cost.liters && <span className="font-data">{cost.liters}L</span>}
                        {cost.hours ? (
                          <span className="font-data">{cost.hours.toLocaleString()} Hrs</span>
                        ) : (
                          cost.odometer && <span className="font-data">{cost.odometer.toLocaleString()} km</span>
                        )}
                        {eff && eff.km != null && <span className="font-data text-gray-400">({eff.km.toLocaleString()} km driven)</span>}
                        {eff && eff.hours != null && <span className="font-data text-gray-400">({eff.hours.toFixed(1)} hrs)</span>}
                        {(cost.supplier_name || cost.vendor) && (
                          <span className="font-medium text-slate-600" data-testid={`cost-supplier-${cost.id}`}>{cost.supplier_name || cost.vendor}</span>
                        )}
                        {cost.invoice_number && (
                          <span className="font-data text-xs bg-white border border-slate-200 px-1.5 py-0.5 rounded">{cost.invoice_number}</span>
                        )}
                        {cost.order_number && (
                          <span className="font-data text-xs bg-white border border-slate-200 px-1.5 py-0.5 rounded" data-testid={`vehicle-cost-order-${cost.id}`}>PO {cost.order_number}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-heading font-bold text-lg text-slate-900 data-value">{fmtR(cost.amount)}</span>
                    <Button variant="ghost" size="icon" onClick={() => { setSelectedCost(cost); setEditCostDialogOpen(true); }} data-testid={`edit-cost-${cost.id}`}><Edit className="w-4 h-4 text-slate-400" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => { setSelectedCost(cost); setDeleteDialogOpen(true); }} data-testid={`delete-cost-${cost.id}`}><Trash2 className="w-4 h-4 text-slate-400" /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 text-slate-400">
            <Fuel className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No cost entries yet</p>
            <Button variant="outline" className="mt-4" onClick={() => setCostDialogOpen(true)} data-testid="add-first-cost-btn">
              <Plus className="w-4 h-4 mr-2" />Add First Cost
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  </>
);

export default VehicleCostsTab;
